import { isIsoDate } from '../validation/is-iso-date.function';
import { isIsoDateTime } from '../validation/is-iso-date-time.function';
import { isIsoDateTimeTz } from '../validation/is-iso-date-time-tz.function';
import type { IsoDate, IsoDateTime, IsoDateTimeTz } from '../../types/iso-date';
import { deconstructDate } from '../helper/deconstruct-date.function';

/**
 * Returns the ISO 8601 week number (1-53) for the given ISO date/date-time input.
 *
 * Supported inputs:
 * - Date-only: YYYY-MM-DD
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM)
 *
 * The calculation is performed in UTC to be deterministic across environments.
 * Throws if the input is not a valid ISO date/date-time string per the above forms.
 */
export function getIsoWeek(input: IsoDate | IsoDateTime | IsoDateTimeTz): number {
  const { year, month, day } = deconstructDate(input);
  if (!year || !month || !day)
    throw new Error('getIsoWeek: invalid ISO date/date-time string');

  // Use UTC to avoid local timezone affecting the week calculation
  const date = new Date(Date.UTC(year, month - 1, day));

  // ISO week algorithm:
  // - Week starts Monday (1) and ends Sunday (7)
  // - Week 1 is the week with January 4th (or the first Thursday) of the ISO year
  // Shift date to Thursday in the same week
  const dayNum = (date.getUTCDay() + 6) % 7; // 0 = Monday, 6 = Sunday
  const thursday = new Date(date);
  thursday.setUTCDate(date.getUTCDate() - dayNum + 3);

  // First Thursday of the ISO year
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const firstThursdayDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDayNum + 3);

  // Compute week number
  return 1 + Math.round(
    (thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000)
  );
}
